/**
 * Kpop Spotlight — shared player pieces: the YouTube embed, the thumbnail
 * fallback and a few animation helpers used by the stage and the card player.
 *
 * Nothing is requested from YouTube until someone presses play, and removing
 * the iframe is what actually stops playback (a hidden player keeps playing).
 */

const YT_ORIGIN = 'https://www.youtube-nocookie.com';

// i.ytimg.com answers a missing maxresdefault with this 120px grey still
// instead of an error, so the width is the only signal.
const PLACEHOLDER_WIDTH = 120;

export const EASE = 'cubic-bezier(.16,1,.3,1)';

export const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** Plain left click, without modifiers: those keep their new-tab meaning. */
export const isPlainClick = (e) => !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0);

export const px = (r) => ({ left: `${r.left}px`, top: `${r.top}px`, width: `${r.width}px`, height: `${r.height}px` });

/** Fade elements in from below, one after the other. */
export function staggerIn(els, delay = 0) {
	els.forEach((el, i) =>
		el.animate([{ opacity: 0, transform: 'translateY(14px)' }, { opacity: 1, transform: 'none' }], {
			duration: 520,
			delay: delay + i * 70,
			easing: EASE,
			fill: 'backwards',
		})
	);
}

/**
 * Put a YouTube player into a frame. The still dims under an equalizer until
 * the player has loaded, then the video fades in over it.
 *
 * @param {HTMLElement} host  The frame (.spot-media).
 * @param {string}      id    YouTube id.
 * @param {string}      title Accessible name for the iframe.
 */
export function embed(host, id, title) {
	host.classList.add('is-loading');
	host.insertAdjacentHTML(
		'beforeend',
		'<span class="spot-eq" aria-hidden="true"><span><i></i><i></i><i></i><i></i><i></i></span></span>'
	);

	const frame = document.createElement('iframe');
	const params = new URLSearchParams({ autoplay: '1', rel: '0', playsinline: '1', modestbranding: '1' });
	frame.src = `${YT_ORIGIN}/embed/${encodeURIComponent(id)}?${params}`;
	frame.title = title || 'YouTube';
	frame.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
	frame.allowFullscreen = true;
	frame.addEventListener(
		'load',
		() => {
			frame.classList.add('is-ready');
			host.querySelector('.spot-eq')?.classList.add('is-done');
		},
		{ once: true }
	);
	host.append(frame);
}

/** Take the player out of a frame again. */
export function unembed(host) {
	host.querySelectorAll('iframe, .spot-eq').forEach((n) => n.remove());
	host.classList.remove('is-loading');
}

/** Swap YouTube's grey placeholder for the hqdefault still that always exists. */
function useFallbackIfPlaceholder(img) {
	if (!img.dataset.fallback) return;
	if (img.naturalWidth === 0 || img.naturalWidth <= PLACEHOLDER_WIDTH) {
		img.src = img.dataset.fallback;
		delete img.dataset.fallback; // one swap only, never a loop
	}
}

/** Watch every YouTube still under root (templates included once cloned). */
export function initStillFallbacks(root = document) {
	root.querySelectorAll('.spot-media img[data-fallback]').forEach((img) => {
		img.addEventListener('error', () => useFallbackIfPlaceholder(img), { once: true });
		img.addEventListener('load', () => useFallbackIfPlaceholder(img), { once: true });
		// A cached image can already be decoded before these listeners attach.
		if (img.complete) useFallbackIfPlaceholder(img);
	});
}
